import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { IndianRupee, Search } from "lucide-react";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 10;

const programsLabel = (s) => {
  const list = s.programs && s.programs.length ? s.programs : (s.program ? [s.program] : []);
  if (!list.length) return "-";
  return list.map((p) => p.name).join(" + ");
};

export default function Fees() {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data));
  }, []);

  const filtered = students.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.phone.includes(q) || s.student_id.toLowerCase().includes(q.toLowerCase()));
  useEffect(() => { setPage(1); }, [q]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalOutstanding = students.reduce((sum, s) => sum + s.fee_summary.outstanding, 0);
  const totalPaid = students.reduce((sum, s) => sum + s.fee_summary.paid, 0);
  const overdueCount = students.filter((s) => s.fee_summary.status === "overdue").length;

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="fees-page">
      <div className="mb-8">
        <div className="label-caps mb-2 flex items-center gap-2"><IndianRupee size={12} /> Finances</div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Fee Management</h1>
        <p className="text-slate-500 mt-1">Track payments, balances and overdue accounts.</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><div className="label-caps mb-2">Total Collected</div><div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }}>{formatINR(totalPaid)}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">Total Outstanding</div><div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>{formatINR(totalOutstanding)}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">Overdue Students</div><div className="text-2xl font-bold text-red-700" style={{ fontFamily: "Chivo" }}>{overdueCount}</div></Card>
      </div>

      <Card className="p-4 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="fee-search" placeholder="Search by name, phone, ID" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps">Student</th>
                <th className="px-4 py-3 label-caps">Program</th>
                <th className="px-4 py-3 label-caps text-right">Total</th>
                <th className="px-4 py-3 label-caps text-right">Paid</th>
                <th className="px-4 py-3 label-caps text-right">Outstanding</th>
                <th className="px-4 py-3 label-caps">Status</th>
                <th className="px-4 py-3 label-caps">Next Due</th>
                <th className="px-4 py-3 label-caps text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paged.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-4">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.student_id}</div>
                  </td>
                  <td className="px-4 py-4">{programsLabel(s)}</td>
                  <td className="px-4 py-4 text-right">{formatINR(s.fee_summary.total)}</td>
                  <td className="px-4 py-4 text-right text-emerald-700 font-medium">{formatINR(s.fee_summary.paid)}</td>
                  <td className="px-4 py-4 text-right font-semibold">{formatINR(s.fee_summary.outstanding)}</td>
                  <td className="px-4 py-4"><Badge variant="outline" className={`status-${s.fee_summary.status}`}>{s.fee_summary.status}</Badge></td>
                  <td className="px-4 py-4 text-xs text-slate-600">{s.fee_summary.next_due ? formatDate(s.fee_summary.next_due.due_date) : "—"}</td>
                  <td className="px-4 py-4 text-right">
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => navigate(`/students/${s.id}`)} data-testid={`view-fee-${s.id}`}>
                      Record Payment
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">No students match your search.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} testIdPrefix="fees-pagination" />
      </Card>
    </div>
  );
}
